import {
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';

import { fetchSupplierRequests } from './fetchSupplierRequests';
import {
  type SupplierRequestsQueryParams,
  type SupplierRequestsResponse,
} from './types';

/**
 * Generates the query options for the supplier requests query so they can be
 * shared between the useQuery hook and prefetching in route loaders.
 *
 * @param queryParams - The company account ID and optional state filter and pagination parameters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and staleTime.
 */
// fallow-ignore-next-line unused-export
export const getSupplierRequestsQueryOptions = <
  TData = SupplierRequestsResponse,
>({
  queryParams,
  options,
}: {
  queryParams: Partial<SupplierRequestsQueryParams>;
  options?: Omit<
    UseQueryOptions<SupplierRequestsResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: queryParams.companyAccountId
      ? async ({ signal }) => fetchSupplierRequests(queryParams, { signal })
      : skipToken,
    queryKey: queryKeysSupplier.getRequests(queryParams),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });

/**
 * React Query based custom hook for fetching the caller's own supplier
 * requests as a paginated list.
 *
 * Requires a company account ID; the query is disabled until it is provided.
 *
 * @param queryParams - The company account ID and optional state filter and pagination parameters.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook containing the supplier requests list.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/get_supplier_requests_suppliers_requests_get
 */
// fallow-ignore-next-line unused-export
export const useQuerySupplierRequests = <TData = SupplierRequestsResponse>(
  queryParams: Partial<SupplierRequestsQueryParams> = {},
  options?: Omit<
    UseQueryOptions<SupplierRequestsResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getSupplierRequestsQueryOptions<TData>({ queryParams, options }));
